import React, { useEffect } from "react";
import { MapPin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  return null;
};

export default function OurFootprint({ footprintData = {} }) {
  const navigate = useNavigate();
  const { title, locations } = footprintData;

  const handleLearnMore = () => {
    navigate("/projects");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 200);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#E8F5FF] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-40 sm:w-64 md:w-72 h-40 sm:h-64 md:h-72 bg-blue-100 rounded-full opacity-30 -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-56 sm:w-80 md:w-96 h-56 sm:h-80 md:h-96 bg-blue-100 rounded-full opacity-30 translate-x-1/2 translate-y-1/2 blur-3xl"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <ScrollToTop />

        {/* Header */}
        <div className="text-center mb-10 sm:mb-14 md:mb-16 lg:mb-20">
          <div className="inline-block mb-4">
            <span className="text-blue-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">
              Our Presence
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-5">
            {title || "Our Footprint"}
          </h2>
          <p className="text-base sm:text-lg md:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            Building trusted communities across South India's most promising
            locations
          </p>
        </div>

        {/* Locations Grid */}
        {locations && locations.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 md:gap-8 lg:gap-10 mb-12 sm:mb-14 md:mb-16">
              {locations.map((loc, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 
                             overflow-hidden border border-blue-50 relative transform hover:-translate-y-2"
                >
                  {/* Background gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-20 sm:w-24 h-20 sm:h-24 bg-blue-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"></div>

                  {/* Card content */}
                  <div className="relative z-10 p-6 sm:p-7 md:p-8 flex flex-col items-center text-center min-h-[240px] sm:min-h-[260px] md:min-h-[280px] justify-center">
                    {/* Icon container */}
                    <div className="mb-5 sm:mb-6 relative">
                      <div className="absolute inset-0 bg-blue-100 rounded-full blur-xl opacity-50 scale-150 group-hover:opacity-75 transition-opacity duration-500"></div>
                      <div className="relative bg-blue-50 p-4 sm:p-5 rounded-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                        <MapPin
                          className="w-8 h-8 sm:w-9 sm:h-9 text-blue-600"
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>

                    {/* Text content */}
                    <div className="space-y-2 sm:space-y-3">
                      <h3 className="font-bold text-lg sm:text-xl md:text-xl text-gray-900 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
                        {loc.name}
                      </h3>
                      <p className="text-sm sm:text-base md:text-base text-gray-600 leading-relaxed max-w-xs mx-auto">
                        {loc.description}
                      </p>
                    </div>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-600 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              ))}
            </div>

            {/* Single Learn More Button */}
            <div className="text-center">
              <button
                onClick={handleLearnMore}
                className="inline-flex items-center justify-center bg-blue-600 text-white text-base sm:text-base font-semibold 
                         px-8 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl 
                         hover:bg-blue-700 hover:shadow-xl transform hover:-translate-y-1 
                         transition-all duration-300 shadow-lg"
              >
                Learn More
              </button>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500 mt-10 sm:mt-14 py-16 sm:py-20">
            <MapPin className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4 opacity-50" />
            <p className="text-base sm:text-lg">No footprint data available.</p>
          </div>
        )}
      </div>
    </section>
  );
}
